import { AddressStatus, IAddressForm, PointType } from '@/models/address';

import { ParsedIDCard } from './idCardParser';

// Drop empty values so merging the built address over an existing form row
// never clobbers populated fields with blanks.
const stripEmpty = (address: Partial<IAddressForm>): Partial<IAddressForm> =>
  Object.fromEntries(
    Object.entries(address).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  ) as Partial<IAddressForm>;

// The parts that make an address scannable; country alone is not enough to
// build or geocode an address.
export const buildScannedAddressString = (
  selectedFields: Partial<ParsedIDCard>,
): string =>
  [
    selectedFields.addressNumber,
    selectedFields.streetAddress1,
    selectedFields.streetAddress2,
    selectedFields.city,
    selectedFields.addressState,
    selectedFields.zipCode,
  ]
    .filter(Boolean)
    .join(', ');

// Builds the address row applied to the person form from a scan. When Google
// geocoding produced a normalized address, its values win for the
// location-derived fields (placeName, zipName, zipcode, state, country) while
// the scanned document wins for the street fields; without geocoding, the raw
// scanned parts are used as-is (and no point is emitted, so a previously
// geocoded point on the row survives the merge).
export const buildScannedAddress = (
  selectedFields: Partial<ParsedIDCard>,
  geocoded: IAddressForm | null,
): Partial<IAddressForm> | null => {
  if (!buildScannedAddressString(selectedFields)) return null;

  if (geocoded) {
    // Use scanned sub-address if available, otherwise fall back to Google's
    const subAddress = selectedFields.streetAddress2 || geocoded.subAddress;

    // Build addressLabel that includes sub-address if present
    let addressLabel = geocoded.addressLabel || '';
    if (
      subAddress &&
      !addressLabel.toLowerCase().includes(subAddress.toLowerCase())
    ) {
      // Insert sub-address after street name but before city
      const parts = addressLabel.split(',');
      if (parts.length > 1) {
        addressLabel = `${parts[0]}, ${subAddress},${parts.slice(1).join(',')}`;
      } else {
        addressLabel = `${addressLabel}, ${subAddress}`;
      }
    }

    return stripEmpty({
      addressNumber: selectedFields.addressNumber || geocoded.addressNumber,
      completeStreetName:
        selectedFields.streetAddress1 || geocoded.completeStreetName,
      subAddress,
      placeName: geocoded.placeName || selectedFields.city,
      zipName: geocoded.zipName || selectedFields.city,
      zipcode: (geocoded.zipcode || selectedFields.zipCode || '').substring(
        0,
        5,
      ),
      state: geocoded.state || selectedFields.addressState,
      country: geocoded.country || selectedFields.country || 'US',
      pointType: geocoded.pointType || PointType.MISCELLANEOUS,
      point: geocoded.point,
      addressLabel,
      normalized: geocoded.point ? 'true' : 'false',
      status: AddressStatus.ACTIVE,
    });
  }

  const city = selectedFields.city;
  const state = selectedFields.addressState;
  const zipCode = selectedFields.zipCode?.substring(0, 5);
  const country = selectedFields.country || 'US';

  // Build addressLabel from scanned parts for form validation
  const addressLabel = [
    selectedFields.addressNumber,
    selectedFields.streetAddress1,
    selectedFields.streetAddress2,
    city,
    state,
    zipCode,
    country,
  ]
    .filter(Boolean)
    .join(', ');

  return stripEmpty({
    addressNumber: selectedFields.addressNumber,
    completeStreetName: selectedFields.streetAddress1,
    subAddress: selectedFields.streetAddress2,
    placeName: city,
    state,
    zipcode: zipCode,
    country,
    addressLabel,
    zipName: city || state || 'Unknown',
    pointType: PointType.MISCELLANEOUS,
    normalized: 'false',
    status: AddressStatus.ACTIVE,
  });
};
